/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Turn the one theme colour an admin picks into the full set of shades the
 * interface needs, and tell them whether their choice is actually readable.
 *
 * The contrast maths here is WCAG 2.1: relative luminance, then the
 * (L1 + 0.05) / (L2 + 0.05) ratio. Thresholds are 4.5:1 for normal body text
 * and 3:1 for large text and user-interface components such as button fills and
 * focus rings.
 */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** Accepts #abc or #aabbcc; returns {r,g,b} 0–255, or null if unparseable. */
export const hexToRgb = (hex) => {
  if (typeof hex !== 'string') return null;
  let value = hex.trim().replace(/^#/, '');
  if (value.length === 3) {
    value = value.split('').map(c => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
};

export const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map(c => clamp(Math.round(c), 0, 255).toString(16).padStart(2, '0')).join('');

export const isValidHex = (hex) => hexToRgb(hex) !== null;

/** WCAG relative luminance. */
const luminance = ({ r, g, b }) => {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** Contrast ratio between two hex colours, 1–21. Returns 0 if either is invalid. */
export const contrastRatio = (hexA, hexB) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 0;
  const la = luminance(a);
  const lb = luminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Black or white, whichever is legible on the given background.
 *
 * This is why only one colour is stored: a pale brand colour automatically gets
 * dark text on its buttons instead of the unreadable white the app would have
 * used if the text colour were hardcoded.
 */
export const readableTextOn = (backgroundHex) => {
  const onWhite = contrastRatio(backgroundHex, '#ffffff');
  const onBlack = contrastRatio(backgroundHex, '#1a1a1a');
  return onWhite >= onBlack ? '#ffffff' : '#1a1a1a';
};

/** Move a colour toward black (amount < 0) or white (amount > 0), -1 to 1. */
export const shade = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const target = amount < 0 ? 0 : 255;
  const weight = Math.abs(amount);
  return rgbToHex({
    r: rgb.r + (target - rgb.r) * weight,
    g: rgb.g + (target - rgb.g) * weight,
    b: rgb.b + (target - rgb.b) * weight
  });
};

/** Same colour at a given alpha, as an rgba() string. */
export const withAlpha = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const DEFAULT_PRIMARY = '#c41e3a';

/**
 * The complete token set derived from one colour. These names are the CSS
 * custom properties the stylesheets use, minus the `--brand-` prefix.
 */
export const deriveBrandTokens = (primaryInput) => {
  const primary = isValidHex(primaryInput) ? primaryInput : DEFAULT_PRIMARY;

  return {
    primary,
    // Hover and pressed states. Darkening a very dark colour does nothing
    // visible, so near-black brands lighten on hover instead.
    'primary-hover': luminance(hexToRgb(primary)) < 0.12 ? shade(primary, 0.18) : shade(primary, -0.14),
    'primary-active': luminance(hexToRgb(primary)) < 0.12 ? shade(primary, 0.28) : shade(primary, -0.26),
    'primary-dark': shade(primary, -0.32),
    'primary-light': shade(primary, 0.4),
    // Tints for selected rows, chips and callout backgrounds.
    'primary-soft': shade(primary, 0.88),
    'primary-softer': shade(primary, 0.94),
    // A deeper tint, for a filled button that has to stand out on a page that
    // already carries the brand colour without shouting as loud as it.
    'primary-tint': shade(primary, 0.74),
    // Text that sits on top of a primary-filled surface.
    'on-primary': readableTextOn(primary),
    // Text-on-white version of the brand colour. A pale brand is unreadable as
    // body text on white, so it darkens until it clears 4.5:1.
    'primary-text': ensureContrast(primary, '#ffffff', 4.5),
    'focus-ring': withAlpha(primary, 0.4),
    'primary-translucent': withAlpha(primary, 0.12)
  };
};

/**
 * Darken (or lighten) a colour just enough to clear a contrast ratio against
 * the given background. Used so the brand colour can be safely applied to text
 * and links whatever the admin picks.
 */
export function ensureContrast(hex, backgroundHex, minRatio) {
  if (!isValidHex(hex)) return hex;
  if (contrastRatio(hex, backgroundHex) >= minRatio) return hex;

  const backgroundIsLight = luminance(hexToRgb(backgroundHex)) > 0.5;
  const direction = backgroundIsLight ? -1 : 1;

  let candidate = hex;
  for (let step = 1; step <= 20; step++) {
    candidate = shade(hex, direction * step * 0.05);
    if (contrastRatio(candidate, backgroundHex) >= minRatio) return candidate;
  }
  return candidate;
}

/**
 * Plain-language verdict on a chosen colour, for the branding editor. Reports
 * what the colour can and cannot be used for rather than a bare pass/fail,
 * because a colour that fails as body text is usually still fine as a button.
 */
export const assessBrandColor = (hex) => {
  if (!isValidHex(hex)) {
    return { valid: false, message: 'Enter a hex colour such as #c41e3a.' };
  }

  const onWhite = contrastRatio(hex, '#ffffff');
  const textColor = readableTextOn(hex);
  const buttonContrast = contrastRatio(hex, textColor);

  const notes = [];
  let level = 'good';

  if (onWhite < 3) {
    level = 'warning';
    notes.push(
      `This colour is too light for text on a white background (${onWhite.toFixed(1)}:1, needs 4.5:1). ` +
      `Links and headings will use a darkened version automatically so they stay readable.`
    );
  } else if (onWhite < 4.5) {
    level = 'notice';
    notes.push(
      `Fine for buttons and borders, but slightly low for small text on white (${onWhite.toFixed(1)}:1, needs 4.5:1). ` +
      `Text using this colour will be darkened automatically.`
    );
  }

  notes.push(
    `Buttons will use ${textColor === '#ffffff' ? 'white' : 'dark'} text on this colour ` +
    `(${buttonContrast.toFixed(1)}:1 — ${buttonContrast >= 4.5 ? 'passes' : 'below'} the 4.5:1 standard).`
  );

  return { valid: true, level, onWhite, buttonContrast, textColor, notes };
};

/**
 * Write the derived tokens onto the document as CSS custom properties. Every
 * stylesheet reads `var(--brand-primary)` and friends, so this one call
 * re-themes the entire application.
 */
export const applyBrandTokens = (primaryInput, target = document.documentElement) => {
  const tokens = deriveBrandTokens(primaryInput);
  for (const [name, value] of Object.entries(tokens)) {
    target.style.setProperty(`--brand-${name}`, value);
  }
  return tokens;
};
